import React from 'react';
import { TextField } from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';

import { DisplayItem } from '../../../types';
import InputProps from '../InputProps';
import {
  generateProperties,
  renderOption,
  OptimizedListBox,
  DropDownIcon,
} from '../helpers';
import BaseInputWrapper from '../BaseInputWrapper';

interface SearchableDropDownProps extends InputProps<string, DisplayItem> {
  /** The list of display options */
  options: DisplayItem[];
  /** What to display when there is nothing selected */
  placeholder?: string;
  /** If true, it will use an optimized version of the dropdown */
  optimized?: boolean;
}

const offsetFromBaseInput = 10;

const SearchableDropDown = ({
  inputId,
  inputName,
  inputLabel,
  options,
  onChange,
  inputError = false,
  inputErrorMessage = '',
  isDisabled = false,
  defaultValue = '',
  placeholder = '',
  optimized = false,
  dataSource,
}: SearchableDropDownProps): JSX.Element => {
  const formInputProps = generateProperties<string, DisplayItem>({
    inputName,
    onChange,
    defaultValue,
    inputError,
    inputErrorMessage,
    dataSource,
  });

  function handleChange(
    _: React.ChangeEvent<{ value: unknown }>,
    newValue: DisplayItem | null,
  ): void {
    if (newValue) {
      formInputProps.onChange(newValue);
    }
  }

  function optionForDefaultValue(): DisplayItem | null {
    const foundOption = options
      .filter((item): boolean => item.Value === formInputProps.value)
      .pop();
    if (foundOption) {
      return foundOption;
    }
    return null;
  }

  function getOptionLabel(option: DisplayItem): string {
    if (typeof option.Text === 'string') {
      return option.Text;
    }
    return option.Value;
  }

  return (
    <BaseInputWrapper
      inputId={inputId}
      label={inputLabel}
      error={formInputProps.error}
      errorMessage={formInputProps.errorText}
      disabled={isDisabled}
    >
      <Autocomplete
        id={inputId}
        options={options}
        getOptionLabel={getOptionLabel}
        disableListWrap
        ListboxComponent={
          optimized
            ? (OptimizedListBox as React.ComponentType<
                React.HTMLAttributes<HTMLElement>
              >)
            : undefined
        }
        renderOption={(option): React.ReactNode =>
          renderOption(option, option.Value === defaultValue)
        }
        getOptionSelected={(option, value): boolean =>
          option.Value === value.Value
        }
        disabled={isDisabled}
        disableClearable
        value={optionForDefaultValue()}
        onChange={handleChange}
        popupIcon={<DropDownIcon />}
        renderInput={(props): React.ReactNode => (
          <TextField
            // Official documentation performs prop-spreading
            // eslint-disable-next-line react/jsx-props-no-spreading
            {...props}
            placeholder={placeholder}
            label=" "
            style={{
              marginLeft: -offsetFromBaseInput,
              width: `calc(100% + ${2 * offsetFromBaseInput}px)`,
            }}
          />
        )}
      />
    </BaseInputWrapper>
  );
};

export default SearchableDropDown;
